import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    FC,
    ReactNode
} from 'react'
import type { LucideIcon } from 'lucide-react'

import { ArrowRight, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TransitionLink } from '@/components/generals/transition-link'

type ButtonVariant = 'primary' | 'dark' | 'light' | 'outline' | 'outline-light' | 'glass'

type ButtonSize = 'sm' | 'md' | 'lg'

type ButtonIcon = 'arrow' | 'phone' | 'none' | LucideIcon

interface ButtonStyleProps {
    variant?: ButtonVariant
    size?: ButtonSize
    icon?: ButtonIcon
    className?: string
    children: ReactNode
}

const variantClasses: Record<ButtonVariant, string> = {
    primary: 'bg-ember text-white hover:bg-ember-deep shadow-[0_14px_34px_-16px_rgba(30,58,92,0.6)]',
    dark: 'bg-ink text-cream hover:bg-graphite',
    light: 'bg-cream text-ink hover:bg-white',
    outline: 'border border-ink/15 text-ink hover:border-ink/40 hover:bg-ink/[0.03]',
    'outline-light': 'border border-cream/25 text-cream hover:border-cream/60 hover:bg-cream/[0.06]',
    glass: 'border border-white/15 bg-white/10 text-cream backdrop-blur-md hover:bg-white/[0.18]'
}

const iconClasses: Record<ButtonVariant, string> = {
    primary: 'bg-white/15 text-white',
    dark: 'bg-cream text-ink',
    light: 'bg-ink text-cream',
    outline: 'bg-ink text-cream',
    'outline-light': 'bg-cream text-ink',
    glass: 'bg-cream text-ink'
}

const sizeClasses: Record<ButtonSize, { withIcon: string, plain: string, icon: string }> = {
    sm: { withIcon: 'h-10 gap-2.5 pl-4 pr-1.5 text-[13px]', plain: 'h-10 px-4 text-[13px]', icon: 'size-7' },
    md: { withIcon: 'h-12 gap-3 pl-5 pr-1.5 text-[15px]', plain: 'h-12 px-6 text-[15px]', icon: 'size-9' },
    lg: { withIcon: 'h-14 gap-3.5 pl-6 pr-2 text-[15px] sm:text-base', plain: 'h-14 px-7 text-[15px] sm:text-base', icon: 'size-10' }
}

const resolveIcon = (icon: ButtonIcon): LucideIcon | null => {
    if (icon === 'none') return null
    if (icon === 'arrow') return ArrowRight
    if (icon === 'phone') return Phone

    return icon
}

const buttonClassName = (variant: ButtonVariant, size: ButtonSize, hasIcon: boolean, className?: string): string =>
    cn(
        'group/button relative inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full font-medium tracking-[-0.01em] transition-[background-color,border-color,color,box-shadow,transform,translate,scale,rotate] duration-300 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50',
        variantClasses[variant],
        hasIcon ? sizeClasses[size].withIcon : sizeClasses[size].plain,
        className
    )

const ButtonContent: FC<{ icon: LucideIcon | null, variant: ButtonVariant, size: ButtonSize, children: ReactNode }> = ({
    icon: Icon,
    variant,
    size,
    children
}): ReactNode => {
    return (
        <>
            <span className='relative block overflow-hidden py-0.5'>
                <span className='block transition-transform duration-500 ease-expo group-hover/button:-translate-y-[115%]'>
                    {children}
                </span>

                <span
                    aria-hidden='true'
                    className='absolute inset-0 block translate-y-[115%] py-0.5 transition-transform duration-500 ease-expo group-hover/button:translate-y-0'
                >
                    {children}
                </span>
            </span>

            {Icon && (
                <span
                    className={cn(
                        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-full',
                        sizeClasses[size].icon,
                        iconClasses[variant]
                    )}
                >
                    <Icon className='size-4 transition-transform duration-500 ease-expo group-hover/button:translate-x-[200%]' aria-hidden='true' />
                    <Icon className='absolute size-4 -translate-x-[200%] transition-transform duration-500 ease-expo group-hover/button:translate-x-0' aria-hidden='true' />
                </span>
            )}
        </>
    )
}

interface ButtonLinkProps extends ButtonStyleProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children' | 'className'> {
    href: string
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    variant = 'primary',
    size = 'md',
    icon = 'arrow',
    className,
    children,
    ...rest
}): ReactNode => {
    const Icon = resolveIcon(icon)

    return (
        <TransitionLink href={href} className={buttonClassName(variant, size, Boolean(Icon), className)} {...rest}>
            <ButtonContent icon={Icon} variant={variant} size={size}>
                {children}
            </ButtonContent>
        </TransitionLink>
    )
}

interface ButtonProps extends ButtonStyleProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> {}

export const Button: FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    icon = 'arrow',
    className,
    children,
    type = 'button',
    ...rest
}): ReactNode => {
    const Icon = resolveIcon(icon)

    return (
        <button type={type} className={buttonClassName(variant, size, Boolean(Icon), className)} {...rest}>
            <ButtonContent icon={Icon} variant={variant} size={size}>
                {children}
            </ButtonContent>
        </button>
    )
}
